/**
 * WebSocket client for Jarvis Console v2
 * Handles real-time communication with exponential backoff reconnection.
 * Ported from ws-client.js with typed events and proper cleanup.
 */

import { signal } from '@preact/signals';
import { api } from '../api/client';
import type { WSMessage } from '../api/types';

// --- Public signals ---
export const wsConnected = signal(false);
export const wsReconnectInfo = signal<{ attempt: number; max: number } | null>(null);

// --- Types ---
type MessageHandler = (data: WSMessage) => void;

// --- State ---
let socket: WebSocket | null = null;
let authenticated = false;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 20;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let connectionWatchdogTimer: ReturnType<typeof setTimeout> | null = null;
let lastPong = 0;
let socketGeneration = 0;
let wakeProbeTimer: ReturnType<typeof setTimeout> | null = null;
let visibilityHandlersSetUp = false;
let foregroundReconnectPending = false;
const pendingMessages: string[] = [];
const messageHandlers = new Set<MessageHandler>();
const HEARTBEAT_STALE_MS = 30000;
const WAKE_PROBE_DEADLINE_MS = 4000;
const CONNECTION_WATCHDOG_MS = 10000;

// --- Public API ---

export function connect() {
  cancelWakeProbe();
  cancelConnectionWatchdog();
  stopHeartbeat();
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  const generation = ++socketGeneration;

  // Close stale socket
  if (socket) {
    try {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onclose = null;
      socket.onerror = null;
      socket.close();
    } catch (_) { /* ignore */ }
    socket = null;
  }
  authenticated = false;
  wsConnected.value = false;
  foregroundReconnectPending = true;

  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  // No token in the URL — query strings land in Caddy's access log. Auth is
  // a first-frame handshake instead (same protocol as the voice endpoint).
  const url = `${protocol}//${location.host}/ws`;

  try {
    socket = new WebSocket(url);
  } catch (e) {
    console.error('[ws] Connection error:', e);
    foregroundReconnectPending = false;
    scheduleReconnect();
    return;
  }
  const currentSocket = socket;
  armConnectionWatchdog(currentSocket, generation);

  currentSocket.onopen = () => {
    if (socket !== currentSocket || generation !== socketGeneration) return;
    console.log('[ws] Connected');
    // The FIRST frame must be the auth handshake — the server closes the
    // socket (4401) if anything else arrives first.
    currentSocket.send(JSON.stringify({ type: 'auth', token: api.getToken() }));
    armConnectionWatchdog(currentSocket, generation);
  };

  currentSocket.onmessage = (event) => {
    if (socket !== currentSocket || generation !== socketGeneration) return;
    try {
      const data = JSON.parse(event.data) as WSMessage;
      if (data.type === 'pong') {
        lastPong = Date.now();
        foregroundReconnectPending = false;
        cancelWakeProbe();
        drainPendingMessages(currentSocket);
        messageHandlers.forEach((handler) => handler({ type: 'transport_ready' } as WSMessage));
        return;
      }
      if (data.type === 'auth_ok') {
        cancelConnectionWatchdog();
        authenticated = true;
        wsConnected.value = true;
        reconnectAttempts = 0;
        wsReconnectInfo.value = null;
        lastPong = Date.now();
        foregroundReconnectPending = false;
        cancelWakeProbe();
        startHeartbeat();

        // Requests are not safe to release at TCP open. Authentication can
        // still fail at that point, and the server will close the socket.
        drainPendingMessages(currentSocket);
        return;
      }
      messageHandlers.forEach((handler) => handler(data));
    } catch (e) {
      console.error('[ws] Parse error:', e);
    }
  };

  currentSocket.onclose = (event) => {
    if (socket !== currentSocket || generation !== socketGeneration) return;
    console.log('[ws] Disconnected:', event.code, event.reason);
    wsConnected.value = false;
    authenticated = false;
    foregroundReconnectPending = false;
    cancelWakeProbe();
    cancelConnectionWatchdog();
    stopHeartbeat();
    scheduleReconnect();
  };

  currentSocket.onerror = (error) => {
    if (socket !== currentSocket || generation !== socketGeneration) return;
    console.error('[ws] Error:', error);
  };
}

export function send(content: string): boolean {
  const msg = JSON.stringify({
    type: 'message',
    content,
    timestamp: new Date().toISOString(),
  });

  const currentSocket = socket;
  if (!canSendApplicationTraffic(currentSocket)) {
    enqueuePending(msg);
    return false;
  }

  currentSocket.send(msg);
  return true;
}

/**
 * Send a chat message over the persistent WS. The server streams chunks back
 * via the same WS (multiplexed with a `chat_id`). Callers should not wait on
 * a response here — chunks arrive via the `onMessage` handler.
 */
export function sendChatMessage(
  chatId: number,
  content: string,
  attachments?: Array<{ type: string; name: string; url?: string }>,
  effort?: string,
  clientMessageId?: string,
  options?: { planFirst?: boolean },
): boolean {
  const payload: Record<string, unknown> = {
    type: 'message',
    chat_id: chatId,
    content,
    timestamp: new Date().toISOString(),
  };
  if (attachments && attachments.length > 0) payload.attachments = attachments;
  if (effort) payload.effort = effort;
  if (clientMessageId) payload.client_message_id = clientMessageId;
  // PH-02: this turn starts in plan mode (relay tabs only; the composer hides the switch elsewhere).
  if (options?.planFirst === true) payload.plan_first = true;

  const msg = JSON.stringify(payload);

  const currentSocket = socket;
  if (!canSendApplicationTraffic(currentSocket)) {
    enqueuePending(msg);
    return false;
  }
  currentSocket.send(msg);
  return true;
}

/**
 * Subscribe to chat replay. For each entry, the server replays any buffered
 * chunks with seq > last_seq — used on (re)connect to catch up anything the
 * client missed while disconnected.
 */
export function sendSubscribe(
  subs: Array<{ chat_id: number; last_seq: number }>,
): boolean {
  if (!subs || subs.length === 0) return true;
  const msg = JSON.stringify({ type: 'subscribe', chats: subs });

  const currentSocket = socket;
  if (!canSendApplicationTraffic(currentSocket)) {
    enqueuePending(msg);
    return false;
  }
  currentSocket.send(msg);
  return true;
}

export function disconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  cancelWakeProbe();
  cancelConnectionWatchdog();
  stopHeartbeat();
  socketGeneration++;
  if (socket) {
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
    socket.close();
    socket = null;
  }
  wsConnected.value = false;
  authenticated = false;
  foregroundReconnectPending = false;
  pendingMessages.length = 0;
}

const MAX_PENDING_MESSAGES = 100;

function enqueuePending(message: string): void {
  if (pendingMessages.length >= MAX_PENDING_MESSAGES) {
    console.error('[ws] Outbox full; preserving existing queued requests');
    return;
  }
  pendingMessages.push(message);
}

export function cancelQueuedChatMessages(chatId: number): number {
  let removed = 0;
  for (let i = pendingMessages.length - 1; i >= 0; i--) {
    try {
      const payload = JSON.parse(pendingMessages[i]) as { chat_id?: unknown };
      if (payload.chat_id !== chatId) continue;
      pendingMessages.splice(i, 1);
      removed++;
    } catch {
      // Non-chat legacy frames have no cancellable chat ownership.
    }
  }
  return removed;
}

export function forceReconnect() {
  console.log('[ws] Force reconnect');
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  cancelWakeProbe();
  reconnectAttempts = 0;
  connect();
}

export function onMessage(handler: MessageHandler): () => void {
  messageHandlers.add(handler);
  return () => {
    messageHandlers.delete(handler);
  };
}

export function setupVisibilityHandler(): () => void {
  if (visibilityHandlersSetUp) return () => {};
  visibilityHandlersSetUp = true;
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      verifyForegroundLiveness();
    }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('online', verifyForegroundLiveness);
  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('online', verifyForegroundLiveness);
    cancelWakeProbe();
    visibilityHandlersSetUp = false;
  };
}

// --- Internal ---

function scheduleReconnect() {
  if (reconnectTimer) clearTimeout(reconnectTimer);

  let delay: number;
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    delay = 30000; // slow poll, never give up
  } else {
    delay = Math.min(500 * Math.pow(2, reconnectAttempts), 15000);
  }

  reconnectAttempts++;
  wsReconnectInfo.value = { attempt: reconnectAttempts, max: MAX_RECONNECT_ATTEMPTS };
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
}

function startHeartbeat() {
  stopHeartbeat();
  lastPong = Date.now();
  heartbeatTimer = setInterval(() => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      if (Date.now() - lastPong > HEARTBEAT_STALE_MS) {
        console.warn('[ws] No pong in 30s — reconnecting');
        forceReconnect();
        return;
      }
      socket.send(JSON.stringify({ type: 'ping' }));
    }
  }, 15000);
}

function canSendApplicationTraffic(targetSocket: WebSocket | null): targetSocket is WebSocket {
  return Boolean(
    targetSocket
    && targetSocket === socket
    && targetSocket.readyState === WebSocket.OPEN
    && authenticated
    && !wakeProbeTimer,
  );
}

function drainPendingMessages(targetSocket: WebSocket): void {
  if (
    socket !== targetSocket
    || targetSocket.readyState !== WebSocket.OPEN
    || !authenticated
    || wakeProbeTimer
  ) {
    return;
  }
  while (pendingMessages.length > 0) {
    targetSocket.send(pendingMessages.shift()!);
  }
}

function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

function verifyForegroundLiveness(): void {
  const currentSocket = socket;
  if (
    !authenticated
    || !wsConnected.value
    || !currentSocket
    || currentSocket.readyState !== WebSocket.OPEN
    || Date.now() - lastPong > HEARTBEAT_STALE_MS
  ) {
    if (!foregroundReconnectPending) {
      foregroundReconnectPending = true;
      forceReconnect();
    }
    return;
  }

  cancelWakeProbe();
  const generation = socketGeneration;
  const pongBeforeProbe = lastPong;
  currentSocket.send(JSON.stringify({ type: 'ping' }));
  wakeProbeTimer = setTimeout(() => {
    wakeProbeTimer = null;
    if (
      generation !== socketGeneration
      || socket !== currentSocket
      || !authenticated
      || currentSocket.readyState !== WebSocket.OPEN
    ) {
      return;
    }
    if (lastPong <= pongBeforeProbe) {
      foregroundReconnectPending = true;
      forceReconnect();
    }
  }, WAKE_PROBE_DEADLINE_MS);
}

function cancelWakeProbe(): void {
  if (!wakeProbeTimer) return;
  clearTimeout(wakeProbeTimer);
  wakeProbeTimer = null;
}

function armConnectionWatchdog(targetSocket: WebSocket, generation: number): void {
  cancelConnectionWatchdog();
  connectionWatchdogTimer = setTimeout(() => {
    connectionWatchdogTimer = null;
    if (
      socket !== targetSocket
      || generation !== socketGeneration
      || authenticated
    ) {
      return;
    }
    console.warn('[ws] Connect/auth timeout, reconnecting');
    foregroundReconnectPending = false;
    forceReconnect();
  }, CONNECTION_WATCHDOG_MS);
}

function cancelConnectionWatchdog(): void {
  if (!connectionWatchdogTimer) return;
  clearTimeout(connectionWatchdogTimer);
  connectionWatchdogTimer = null;
}
